import type {
  CompanyJobEmployment,
  CompanyJobExperienceLevel,
  CompanyJobSummary,
  CompanyJobWorkType,
} from '@tabbio/types';
import { COMPANY_PAGE_DISTRIBUTIONS } from '@tabbio/types';
import type { TFunction } from 'i18next';
import {
  DEPARTMENT_OPTIONS,
  EMPLOYMENT_OPTIONS,
  EXPERIENCE_LEVEL_OPTIONS,
  WORK_TYPE_OPTIONS,
  getOptionLabel,
  type OptionItem,
} from '../components/jobs/post-job-flow.data';
import type { PublicProfileOpenRole } from './public-profile-builder';

function titleize(value: string) {
  return value
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

const EMPLOYMENT_API_TO_DRAFT: Record<string, CompanyJobEmployment> = {
  'full-time': 'full_time',
  'part-time': 'part_time',
  contract: 'contract',
  internship: 'internship',
};

const EXPERIENCE_API_TO_DRAFT: Record<string, CompanyJobExperienceLevel> = {
  entry: 'entry_0_2',
  mid: 'mid_2_5',
  senior: 'senior_5_8',
  lead: 'lead_8_12',
  executive: 'executive_12_plus',
};

function translateOptionValue(
  t: TFunction<'common'>,
  options: OptionItem[],
  value?: string | null,
) {
  if (!value) {
    return null;
  }

  const labelKey = getOptionLabel(options, value);
  return labelKey ? t(labelKey as never) : titleize(value);
}

export function mapJobWorkModeToOpenRoleRemote(
  value: string,
): PublicProfileOpenRole['remote'] {
  if (value === 'hybrid') return 'hybrid';
  if (value === 'remote') return 'remote';
  return 'on-site';
}

export function getCompanyJobDepartmentLabel(
  t: TFunction<'common'>,
  department?: string | null,
) {
  return translateOptionValue(t, DEPARTMENT_OPTIONS, department) || t('jobPost.list.generalDepartment');
}

export function getCompanyJobEmploymentLabel(
  t: TFunction<'common'>,
  type: string,
) {
  return (
    translateOptionValue(t, EMPLOYMENT_OPTIONS, EMPLOYMENT_API_TO_DRAFT[type] ?? type) ||
    titleize(type)
  );
}

export function getCompanyJobWorkModeLabel(
  t: TFunction<'common'>,
  workMode: string,
) {
  return (
    translateOptionValue(t, WORK_TYPE_OPTIONS, workMode as CompanyJobWorkType) ||
    titleize(workMode)
  );
}

export function getCompanyJobExperienceLabel(
  t: TFunction<'common'>,
  level: string,
) {
  return (
    translateOptionValue(
      t,
      EXPERIENCE_LEVEL_OPTIONS,
      EXPERIENCE_API_TO_DRAFT[level] ?? level,
    ) || titleize(level)
  );
}

export function getCompanyJobMetaLine(
  t: TFunction<'common'>,
  job: Pick<CompanyJobSummary, 'department' | 'location' | 'workMode' | 'type'>,
) {
  return [
    getCompanyJobDepartmentLabel(t, job.department),
    job.location || null,
    getCompanyJobWorkModeLabel(t, job.workMode),
    getCompanyJobEmploymentLabel(t, job.type),
  ]
    .filter(Boolean)
    .join(' · ');
}

export function companyJobToOpenRole(
  job: CompanyJobSummary,
  t: TFunction<'common'>,
): PublicProfileOpenRole {
  return {
    id: job.id,
    title: job.title,
    department: getCompanyJobDepartmentLabel(t, job.department),
    location: job.location || t('jobPost.list.remoteFallback'),
    remote: mapJobWorkModeToOpenRoleRemote(job.workMode),
    type: getCompanyJobEmploymentLabel(t, job.type),
    applications: job.applicationCount,
    enabled: job.status === 'active',
  };
}

/**
 * Would the public company page actually render this role (PRD §7.5)?
 *
 * Mirrors the API's company-page query: live, distributed to the company page,
 * and not masked. A confidential role is excluded on both counts now - its
 * distribution is `tabbio_only` - but the visibility check stays as the
 * belt-and-braces guard for rows written before that was true.
 */
export function isCompanyPageRole(job: CompanyJobSummary): boolean {
  return (
    job.status === 'active' &&
    COMPANY_PAGE_DISTRIBUTIONS.includes(
      job.distribution ?? 'tabbio_and_company',
    ) &&
    job.visibility !== 'confidential'
  );
}

// Counts every live role, company page or not: this feeds the employer's own
// jobs dashboard, where a confidential role is still one of their roles.
export function getCompanyJobsStats(jobs: CompanyJobSummary[]) {
  return {
    rolesCount: jobs.filter((job) => job.status === 'active').length,
    totalApplications: jobs.reduce((sum, job) => sum + job.applicationCount, 0),
  };
}

export function formatCompanyJobStatus(t: TFunction<'common'>, value: string) {
  return t(`jobPost.list.status.${value}` as never, {
    defaultValue: titleize(value),
  });
}

export type CompanyJobLifecycle = 'draft' | 'live' | 'offline' | 'archived';

/**
 * PRD §7.6 lifecycle from the stored status: draft → Draft, active → Live,
 * closed → Offline, archived → Archived. Distribution is a separate axis and
 * never changes the lifecycle label.
 */
export function getCompanyJobLifecycle(status: string): CompanyJobLifecycle {
  if (status === 'draft') return 'draft';
  if (status === 'closed') return 'offline';
  if (status === 'archived') return 'archived';
  return 'live';
}

export function formatCompanyJobLifecycle(
  t: TFunction<'common'>,
  status: string,
) {
  const lifecycle = getCompanyJobLifecycle(status);
  return t(`employer:jobFlow.status.${lifecycle}` as never, {
    defaultValue: titleize(lifecycle),
  }) as string;
}
